import "./styles.scss"

import React, { useState } from "react"
import { Link } from "gatsby"
import MailchimpSubscribe from "react-mailchimp-subscribe"

import { useLocale } from "context/locale"

import Button from "components/Button"
import Checkbox from "components/Checkbox"
import LangSwitcher from "components/LangSwitcher"
import Badge from "../Badge"

const url =
  "https://webinity.us10.list-manage.com/subscribe/post?u=84687b1d3c0ee3b7f8050cd9e&amp;id=9f3833cc15&amp;f_id=004ed7e5f0"

const NewsletterForm = ({ status, message, onValidated }) => {
  const { t } = useLocale()

  const [formData, setFormData] = useState({
    email: "",
    legal: false,
  })

  const submit = e => {
    e.preventDefault()
    formData.email &&
      formData.email.indexOf("@") > -1 &&
      onValidated({
        EMAIL: formData.email,
      })
  }

  return (
    <form>
      <div className="input fill input--dark no-m">
        <input
          className="input__input"
          type="email"
          id="email"
          name="email"
          placeholder={t("john.doe@example.com")}
          value={formData.email}
          onChange={e =>
            setFormData(prevData => ({
              ...prevData,
              email: e.target.value,
            }))
          }
        />
      </div>
      <Button
        className="button--small button--dark"
        type="submit"
        disabled={!formData.legal}
        onClick={submit}
      >
        {t("Subscribe")}
      </Button>
      <Checkbox
        className="checkbox--dark"
        name="legal"
        checked={formData.legal}
        onChange={e =>
          setFormData(prevData => ({
            ...prevData,
            legal: e.target.checked,
          }))
        }
        required
      >
        <span>*</span>
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              "I accept Webinity's <a href='/privacy-policy/'>Privacy Policy</a> and understand that providing the data indicated in the form is voluntary and required to receive updates with latest news."
            ),
          }}
        />
      </Checkbox>
      {status === "sending" && (
        <div className="footer__newsletter-info" style={{ color: "blue" }}>
          {t("sending")}...
        </div>
      )}
      {status === "error" && (
        <div
          className="footer__newsletter-info"
          style={{ color: "red" }}
          dangerouslySetInnerHTML={{ __html: t(message) }}
        />
      )}
      {status === "success" && (
        <div
          className="footer__newsletter-info"
          style={{ color: "green" }}
          dangerouslySetInnerHTML={{ __html: t(message) }}
        />
      )}
    </form>
  )
}

const Footer = ({ services }) => {
  const { t } = useLocale()

  const servicesSorted = services
    ?.map(service => service.services.nodes)
    .reduce((accumulator, current) => {
      return [...accumulator, ...current]
    }, [])
    .sort((a, b) => {
      const a_order = a?.acfServices?.servicesOrder?.home
      const b_order = b?.acfServices?.servicesOrder?.home

      if (a_order && b_order) {
        return a_order - b_order
      } else {
        return a.title > b.title ? 1 : -1
      }
    })

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__newsletter">
          <h2>{t("Join our newsletter")}</h2>
          <div
            className="footer__newsletter-description"
            dangerouslySetInnerHTML={{
              __html: `<p>${t(
                "Stay up to date with the latest news and trends for WordPress<br/> and WooCommerce based websites."
              )}</p>`,
            }}
          />
          <MailchimpSubscribe
            url={url}
            render={({ subscribe, status, message }) => (
              <NewsletterForm
                status={status}
                message={message}
                onValidated={formData => subscribe(formData)}
              />
            )}
          />
        </div>

        <div className="footer__logo">
          <img src={require("assets/images/logo.svg").default} alt="" />
        </div>

        <div className="row">
          <div className="col-12 col-md-5 col-lg-6">
            <div className="footer__col">
              <address>
                Fryderyka Chopina 18/3A
                <br />
                30-047 {t("Krakow")}
                <br />
                {t("Poland")}
              </address>

              <div className="footer__contact">
                <p>
                  {t("Have questions?")}
                  <br />
                  {t("Contact us:")}
                </p>
                <ul>
                  <li>
                    <a href="tel:+48690888692">+48 690 888 692</a>
                  </li>
                  <li>
                    <a href="mailto:contact@webinity.com">
                      contact@webinity.com
                    </a>
                  </li>
                </ul>
              </div>

              <div className="d-none d-md-block">
                <FooterLegal t={t} />
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3 col-lg-2">
            <Link to={t("/pricing/")} className="footer__link">
              {t("Pricing")}
            </Link>
            {servicesSorted && (
              <>
                <p className="footer__link">{t("Services")}</p>
                <div className="footer__nav">
                  {servicesSorted.map(service => (
                    <Link
                      key={service.title}
                      to={`${t("/services/")}${service.slug}`}
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="col-6 col-md-3 offset-md-1 col-lg-2">
            <Link to={t("/faq/")} className="footer__link">
              {t("FAQ")}
            </Link>
            <Link to={t("/testimonials/")} className="footer__link">
              {t("Testimonials")}
            </Link>
            <p className="footer__link">{t("Company")}</p>
            <div className="footer__nav">
              <Link to={t("/company/mission/")}>{t("Mission")}</Link>
              <Link to={t("/company/careers/")}>{t("Careers")}</Link>
              <Link to={t("/company/media-and-press-materials/")}>
                {t("Media & Press")}
              </Link>
              <Link to={t("/company/roadmap/")}>{t("Roadmap")}</Link>
              <Link to={t("/company/become-a-partner/")}>
                {t("Become a Partner")}
              </Link>
              <Link to={t("/company/grants/")}>{t("Grants")}</Link>
            </div>
            <Link to={t("/blog/")} className="footer__link">
              {t("Blog")}
            </Link>
            <Link to={t("/contact/")} className="footer__link">
              {t("Contact")}
            </Link>
            <Badge />
          </div>

          <div className="col-12 d-md-none">
            <FooterLegal t={t} />
          </div>
        </div>
      </div>
    </footer>
  )
}

const FooterLegal = ({ t }) => {
  return (
    <div className="footer__legal">
      <div className="row flex-row flex-md-column-reverse">
        <div className="col-6 col-md-12">
          <div className="footer__nav">
            <Link to={t("/privacy-policy/")}>{t("Privacy Policy")}</Link>
            <Link to={t("/cookies-policy/")}>{t("Cookies Policy")}</Link>
            <Link to={t("/refund-policy/")}>{t("Refund Policy")}</Link>
            <Link to={t("/terms-of-use/")}>{t("Terms of Use")}</Link>
          </div>
        </div>
        <div className="col-6 col-md-12">
          <LangSwitcher />
        </div>
      </div>

      <p className="footer__copy">
        &copy; {new Date().getFullYear()} Webinity. {t("All rights reserved.")}
      </p>
    </div>
  )
}

export default Footer
